import StarsIcon from '../../assets/icons/stars.svg?react';
import Button from '../../components/common/button.tsx';
import { SubmitHandler, useForm } from 'react-hook-form';
import { generateSlug } from 'random-word-slugs';
import {
  Maybe,
  QuizFragmentFragment,
  QuizStatus,
} from '../../apollo/__generated__/graphql.ts';
import { useMutation } from '@apollo/client';
import {
  CREATE_QUIZ,
  QUIZ_FRAGMENT,
  UPDATE_QUIZ,
} from '../../apollo/quizzes.ts';
import { toast } from 'react-toastify';
import { useFragment } from '../../apollo/__generated__';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { compact } from 'lodash';

interface IForm {
  name: string;
  description: string;
}

interface ICreateQuiz {
  quiz?: Maybe<QuizFragmentFragment>;
  onCloseModal?: (slug: string) => void;
  questions?: number[];
}

const CreateQuizForm = ({ quiz, onCloseModal, questions }: ICreateQuiz) => {
  const navigate = useNavigate();
  const [createQuiz, { data: newQuizData }] = useMutation(CREATE_QUIZ);
  const [updateQuiz] = useMutation(UPDATE_QUIZ);
  const newQuiz = useFragment(QUIZ_FRAGMENT, newQuizData?.quiz.create);

  useEffect(() => {
    const slug = newQuiz?.slug;
    if (slug) navigate(`/quizzes-browser/${slug}`);
  }, [navigate, newQuiz]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IForm>({
    defaultValues: {
      name: quiz?.name ? quiz.name : undefined,
      description: quiz?.description ? quiz.description : undefined,
    },
  });

  const onCreate: SubmitHandler<IForm> = (data) => {
    const create = createQuiz({
      variables: {
        input: {
          name: data.name,
          description: data.description,
          orderedQuestions:
            questions?.map((id, index) => ({
              questionId: id,
              order: index,
            })) || [],
          status: QuizStatus.Draft,
        },
      },
    });

    toast.promise(create, {
      pending: 'Creating quiz...',
      success: 'Quiz created',
    });
  };

  const onUpdate: SubmitHandler<IForm> = (data) => {
    if (!quiz?.id) return;
    const currentQuestionsIds =
      quiz.quizQuestions?.map(({ question }) => question?.id) || [];
    const questionIds: number[] = compact(currentQuestionsIds);
    const update = updateQuiz({
      variables: {
        input: {
          id: quiz.id,
          name: data.name,
          description: data.description,
          status: quiz.status,
          orderedQuestions:
            questionIds?.map((id, index) => ({
              questionId: id,
              order: index,
            })) || [],
        },
      },
    });

    toast.promise(update, {
      pending: 'Updating quiz...',
      success: 'Quiz updated',
    });

    update.then((data) => {
      const slug = data.data?.quiz.update.slug;
      if (onCloseModal && slug) onCloseModal(slug);
    });
  };

  const onClickGenerateName = () =>
    setValue('name', generateSlug(3, { format: 'title' }));

  return (
    <form
      className={'w-[500px] p-4'}
      onSubmit={quiz ? handleSubmit(onUpdate) : handleSubmit(onCreate)}
    >
      <div className={'mt-8'}>
        <label htmlFor="name" className={'font-medium'}>
          Name
        </label>
        <div className={'flex items-center'}>
          <input
            type="text"
            placeholder={'e.g. Math Quiz'}
            className={'base-input'}
            {...register('name', { required: true })}
          />
          <Button
            icon={<StarsIcon />}
            white
            tip={'Generate unique name'}
            className={'ml-2'}
            onClick={onClickGenerateName}
          />
        </div>
        {errors.name && (
          <span className={'text-sm text-red'}>This field is required</span>
        )}
        <p className={'description'}>The quiz name must be unique</p>
      </div>

      <div className={'mt-6 flex flex-col'}>
        <label htmlFor="description" className={'font-medium'}>
          Description
        </label>

        <textarea
          placeholder={'Enter Description...'}
          className={'h-32 max-h-80 rounded-lg border border-light-gray p-1'}
          {...register('description', { required: true })}
        />
        {errors.description && (
          <span className={'text-sm text-red'}>This field is required</span>
        )}
      </div>

      <Button
        type={'button'}
        onClick={quiz ? handleSubmit(onUpdate) : handleSubmit(onCreate)}
        className={'mt-8 w-full'}
      >
        {quiz ? 'Save' : 'Create'}
      </Button>
    </form>
  );
};
export default CreateQuizForm;
